// src/components/timeline/TimelineD3Charts.tsx
import { ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { Button, DatePicker } from "antd";
import * as d3 from "d3";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "dayjs/locale/en";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import PubSub from "pubsub-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToken } from "../../hooks/useToken";
import { TInterest } from "../../models/interests.model";
import { TPost } from "../../models/posts.model";
import { useAuth } from "../../providers/UserProvider";
import { TResponse } from "../../services/apiWorker";
import ModalPreviewEvent from "./chart/ModalPreviewEvent";
import { useTimelineChartDate } from "./chart/useTimelineChartDate";
import { useTimelineChartZoom } from "./chart/useTimelineChartZoom";
import { ModalPostEdit } from "./ModalPostEdit";
import { TimelineD3ChartsActions } from "./TimelineD3ChartsActions";
import { TimelineD3ChartsTooltip } from "./TimelineD3ChartsTooltip";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);
dayjs.extend(isBetween);
dayjs.locale("en");

export const TimelineD3View = { SMALL: 185, LARGE: 600 };

type RangeValueType = [Dayjs | null, Dayjs | null] | null;

const zoomLevels = [
  { level: "month", days: 30 },
  { level: "year", months: 12 },
  { level: "decade", years: 10 },
] as const;

export function TimelineD3Charts({
  posts,
  interests,
  selectedCategoryIds,
  setSelectedCategoryIds,
}: {
  posts: TPost[];
  interests: TInterest[];
  selectedCategoryIds: string[];
  setSelectedCategoryIds: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { profile } = useAuth();
  const [viewType, setViewType] =
    useState<keyof typeof TimelineD3View>("SMALL");
  const svgRef = useRef<SVGSVGElement>(null);
  const timelineHeight = TimelineD3View[viewType] || 300;
  const { token, screens } = useToken();
  const [focusedPost, setFocusedPost] = useState<TPost | undefined>(undefined);
  const [selectedRange, setSelectedRange] = useState<RangeValueType>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);
  const [postForPreview, setPostForPreview] = useState<TPost | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [postForEdit, setPostForEdit] = useState<TPost | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [zoomLevelIndex, setZoomLevelIndex] = useState<number>(0);

  // *** ADICIONADO: Estado para a data que a "pinça" representa ***
  const [referenceDate, setReferenceDate] = useState<Dayjs>(() => dayjs());

  const calculateDateRange = (
    centerDate: Dayjs,
    levelIndex: number
  ): { dateMin: Date; dateMax: Date } => {
    const levelConfig = zoomLevels[levelIndex];
    let newDateMin: Dayjs;
    let newDateMax: Dayjs;
    if (levelConfig.level === "month") {
      const halfDays = Math.floor(levelConfig.days / 2);
      newDateMin = centerDate.subtract(halfDays, "day");
      newDateMax = centerDate.add(levelConfig.days - halfDays, "day");
    } else if (levelConfig.level === "year") {
      const halfMonths = Math.floor(levelConfig.months / 2);
      newDateMin = centerDate.subtract(halfMonths, "month");
      newDateMax = centerDate.add(levelConfig.months - halfMonths, "month");
    } else {
      const halfYears = Math.floor(levelConfig.years / 2);
      newDateMin = centerDate.subtract(halfYears, "year");
      newDateMax = centerDate.add(levelConfig.years - halfYears, "year");
    }
    return { dateMin: newDateMin.toDate(), dateMax: newDateMax.toDate() };
  };

  const [dateMin, setDateMin] = useState<Date>(
    () => calculateDateRange(referenceDate, 0).dateMin // Usa referenceDate inicial
  );
  const [dateMax, setDateMax] = useState<Date>(
    () => calculateDateRange(referenceDate, 0).dateMax // Usa referenceDate inicial
  );

  useEffect(() => {
    if (svgRef.current) {
      const updateWidth = () => {
        if (svgRef.current) {
          setChartWidth(svgRef.current.getBoundingClientRect().width);
        }
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      if (svgRef.current.parentElement) {
        resizeObserver.observe(svgRef.current.parentElement);
      }
      return () => resizeObserver.disconnect();
    }
  }, []);

  const width = chartWidth;
  const height = timelineHeight;

  const handleClearAllFilters = () => {
    setSelectedRange(null);
    setSelectedCategoryIds([]);
    PubSub.publish("timeline:set-today");
  };

  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPostForPreview(null);
  };
  const handleEditPreview = () => {
    if (!postForPreview) return;
    setPostForEdit(postForPreview);
    setIsEditModalOpen(true);
    handleClosePreviewModal();
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setPostForEdit(null);
  };
  const handlePostUpdate = (updatedPost: TPost) => {
    console.log("Post updated!", updatedPost);
    handleCloseEditModal();
  };

  const timeFilteredPosts = useMemo(() => {
    let intermediatePosts = posts;
    if (selectedRange && selectedRange[0] && selectedRange[1]) {
      const startDate = selectedRange[0].startOf("day");
      const endDate = selectedRange[1].endOf("day");
      intermediatePosts = posts.filter((post) => {
        const postDate = dayjs(post.eventDate || post.createdAt);
        return postDate.isBetween(startDate, endDate, "day", "[]");
      });
    }
    if (selectedCategoryIds.length > 0) {
      intermediatePosts = intermediatePosts.filter((post) =>
        post.interests?.some((interestId) =>
          selectedCategoryIds.includes(interestId)
        )
      );
    }
    return intermediatePosts;
  }, [posts, selectedRange, selectedCategoryIds]);

  const days = useMemo(
    () =>
      dateMin && dateMax
        ? Math.round(
            (dateMax.getTime() - dateMin.getTime()) / (1000 * 60 * 60 * 24)
          )
        : 0,
    [dateMin, dateMax]
  );

  const interestsResponseForModal: TResponse<TInterest> = useMemo(
    () => ({
      content: interests || [],
      totalElements: interests?.length || 0,
      pageNumber: 0,
      pageSize: interests?.length || 0,
      totalPages: 1,
      isLast: true,
      isFirst: true,
      total: interests?.length || 0,
      offset: 0,
      limit: interests?.length || 20,
    }),
    [interests]
  );

  const isAnyFilterActive = useMemo(
    () => selectedCategoryIds.length > 0 || !!selectedRange,
    [selectedRange, selectedCategoryIds]
  );
  const todayStart = useMemo(() => dayjs().startOf("day"), []);
  // const today = useMemo(() => new Date(), []); // Não é mais usado para posicionar a pinça

  const handleZoom = (newLevelIndex: number, centerDate: Dayjs) => {
    if (newLevelIndex >= 0 && newLevelIndex < zoomLevels.length) {
      const { dateMin: newMin, dateMax: newMax } = calculateDateRange(
        centerDate,
        newLevelIndex
      );
      PubSub.publish("timeline:zoom", { dateMin: newMin, dateMax: newMax });
      setZoomLevelIndex(newLevelIndex);
      // *** ATUALIZADO: Também atualiza a data de referência ao dar zoom ***
      // Se o centro é 'hoje' (vindo dos botões +/- ou set-today), a pinça volta pra hoje
      // Se o centro é outra data (vindo do DatePicker), a pinça vai pra essa data
      setReferenceDate(centerDate);
    }
  };

  const handleZoomOut = () => {
    const todayDate = dayjs();
    setReferenceDate(todayDate); // Reseta pinça para hoje
    handleZoom(zoomLevelIndex + 1, todayDate); // Zoom centrado em hoje
  };

  const handleZoomIn = () => {
    const todayDate = dayjs();
    setReferenceDate(todayDate); // Reseta pinça para hoje
    handleZoom(zoomLevelIndex - 1, todayDate); // Zoom centrado em hoje
  };

  useEffect(() => {
    const tokenToday = PubSub.subscribe("timeline:set-today", () => {
      const todayDate = dayjs();
      const defaultLevelIndex = 0;
      const { dateMin: newMin, dateMax: newMax } = calculateDateRange(
        todayDate,
        defaultLevelIndex
      );
      PubSub.publish("timeline:zoom", { dateMin: newMin, dateMax: newMax });
      setZoomLevelIndex(defaultLevelIndex);
      setSelectedRange(null);
      setReferenceDate(todayDate); // *** ATUALIZADO: Reseta pinça para hoje ***
    });

    const tokenZoom = PubSub.subscribe(
      "timeline:zoom",
      (
        msg,
        { dateMin: newMin, dateMax: newMax }: { dateMin?: Date; dateMax?: Date }
      ) => {
        if (newMin && (!dateMin || newMin.getTime() !== dateMin.getTime())) {
          setDateMin(newMin);
        }
        if (newMax && (!dateMax || newMax.getTime() !== dateMax.getTime())) {
          setDateMax(newMax);
        }
      }
    );
    return () => {
      PubSub.unsubscribe(tokenToday);
      PubSub.unsubscribe(tokenZoom);
    };
  }, [dateMin, dateMax]); // Removido initialCenterDate, adicionado dateMin/Max

  useTimelineChartZoom({
    svgRef,
    width,
    height,
    dateMin,
    dateMax,
    days,
    focusedPost,
  });
  useTimelineChartDate({
    svgRef,
    width,
    height,
    dateMin,
    dateMax,
    _days: days,
  });

  const xScale = useMemo(() => {
    if (!dateMin || !dateMax || width <= 0) {
      const now = new Date();
      return d3
        .scaleTime()
        .domain([now, now])
        .range([0, width - 10]);
    }
    return d3
      .scaleTime()
      .domain([dateMin, dateMax])
      .range([0, width - 10]);
  }, [dateMin, dateMax, width]);

  const yScale = useMemo(() => {
    if (height <= 0 || !interests) {
      return d3
        .scaleLinear()
        .domain([0, 1])
        .range([height - 10, 40]);
    }
    const numInterests = interests?.length || 0;
    return d3
      .scaleLinear()
      .domain([-1, numInterests + (numInterests > 0 ? 0 : 1)])
      .range([height - 10, 40]);
  }, [height, interests]);

  useEffect(() => {
    if (
      !svgRef.current ||
      width <= 0 ||
      height <= 0 ||
      !dateMin ||
      !dateMax ||
      !xScale ||
      !yScale ||
      !interests
    )
      return;
    const dotSize = days > 365 ? 2 : 5;
    const svg = d3.select(svgRef.current).select("g.event");
    d3.select(svgRef.current).on("mousemove", null).on("mouseleave", null);
    d3.select(svgRef.current)
      .on("mousemove", function (event) {
        const pointer = d3.pointer(event);
        const mx = pointer[0];
        const my = pointer[1];
        let nearestPost: TPost | undefined = undefined;
        let minDistancePost = 25;
        timeFilteredPosts.forEach((d) => {
          if (!d) return;
          const postDate = dayjs(d.eventDate || d.createdAt).toDate();
          const domain = xScale.domain();
          if (postDate >= domain[0] && postDate <= domain[1]) {
            const interestIndex = interests.findIndex((i) =>
              d.interests?.includes(i.id)
            );
            const yPos = interestIndex !== -1 ? interestIndex + 1 : 0;
            try {
              const cy = yScale(yPos);
              const cx = xScale(postDate);
              const distance = Math.hypot(cx - mx, cy - my);
              if (distance < minDistancePost) {
                minDistancePost = distance;
                nearestPost = d;
              }
            } catch (e) {
              /* Ignore */
            }
          }
        });
        setFocusedPost(nearestPost);
      })
      .on("mouseleave", function () {
        setFocusedPost(undefined);
      });

    const dots = svg
      .selectAll<SVGCircleElement, TPost>("circle.event-dot")
      .data(timeFilteredPosts, (d) => d.id);

    dots
      .enter()
      .append("circle")
      .attr("class", "event-dot")
      .attr("cx", (d) => xScale(dayjs(d.eventDate || d.createdAt).toDate()))
      .attr("cy", (d) => {
        const i = interests.findIndex((i) => d.interests?.includes(i.id));
        return yScale(i !== -1 ? i + 1 : 0);
      })
      .attr("r", 0)
      .attr("fill", (d) => {
        const pD = dayjs(d.eventDate || d.createdAt);
        if (pD.isAfter(todayStart)) return 'transparent';
        const i = interests.find((i) => d.interests?.includes(i.id));
        return i?.color || token.colorError;
      })
      .style("stroke", (d) => {
        const i = interests.find((i) => d.interests?.includes(i.id));
        return i?.color || token.colorError;
      })
      .style("stroke-width", 1)
      .style("cursor", "pointer")
      .style("opacity", 0.8)
      .on("click", function (event, d: TPost) {
        event.stopPropagation();
        setPostForPreview(d);
        setIsPreviewModalOpen(true);
      })
      .transition("enter")
      .duration(300)
      .attr("r", dotSize);

    dots
      .transition("update")
      .duration(300)
      .attr("cx", (d) => xScale(dayjs(d.eventDate || d.createdAt).toDate()))
      .attr("cy", (d) => {
        const i = interests.findIndex((i) => d.interests?.includes(i.id));
        return yScale(i !== -1 ? i + 1 : 0);
      })
      .attr("r", dotSize)
      .attr("fill", (d) => {
        const pD = dayjs(d.eventDate || d.createdAt);
        if (pD.isAfter(todayStart)) return 'transparent';
        const i = interests.find((i) => d.interests?.includes(i.id));
        return i?.color || token.colorError;
      })
      .style("stroke", (d) => {
        const i = interests.find((i) => d.interests?.includes(i.id));
        return i?.color || token.colorError;
      });
    dots.exit().transition("exit").duration(300).attr("r", 0).remove();
  }, [
    timeFilteredPosts,
    dateMin,
    dateMax,
    width,
    height,
    xScale,
    yScale,
    interests,
    token,
    days,
    todayStart,
    focusedPost?.id,
  ]);

  useEffect(() => {
    if (!svgRef.current || width <= 0 || !xScale || !yScale) return;
    const dotSize = days > 365 ? 2 : 5;
    const svg = d3.select(svgRef.current).select("g.event");
    svg
      .selectAll<SVGCircleElement, TPost>("circle.event-dot")
      .transition("focus")
      .duration(150)
      .attr("r", (d) => (focusedPost?.id === d.id ? dotSize * 1.8 : dotSize))
      .style("opacity", (d) => (focusedPost?.id === d.id ? 1 : 0.7))
      .style("stroke-width", (d) => (focusedPost?.id === d.id ? 2 : 1));
  }, [focusedPost?.id, days, width, xScale, yScale]);

  // *** MODIFICADO: Calcula posição da pinça com base em referenceDate ***
  const datePickerPosition = useMemo(() => {
    if (width <= 0 || !dateMin || !dateMax || !xScale || !referenceDate)
      return null; // Adicionado check referenceDate

    const refDate = referenceDate.toDate(); // Converte para Date para a escala
    let referenceX: number;
    try {
      referenceX = xScale(refDate); // Calcula posição X da data de referência
    } catch (e) {
      // Caso a escala ainda não esteja pronta ou a data seja inválida
      return null;
    }

    const isReferenceVisible = referenceDate.isBetween(
      dayjs(dateMin),
      dayjs(dateMax),
      null,
      "[]"
    );

    // Mesmo que não esteja visível, podemos querer mostrar o controle na borda?
    // Por enquanto, só mostra se estiver visível. Ajustar se necessário.
    // if (!isReferenceVisible) return null;

    const datePickerContainerWidth = 230;
    const datePickerTop = 25;
    const leftPosition = referenceX - datePickerContainerWidth / 2;
    const PADDING = 5;
    // Garante que o container fique dentro das bordas do SVG
    const finalLeft = Math.max(
      PADDING,
      Math.min(leftPosition, width - datePickerContainerWidth - PADDING)
    );

    return {
      left: finalLeft,
      top: datePickerTop,
      width: `${datePickerContainerWidth}px`,
      referenceX: referenceX, // Renomeado de todayX para referenceX
    };
    // *** Depende de referenceDate agora, não mais de 'today' ***
  }, [width, dateMin, dateMax, xScale, referenceDate]);

  // *** MODIFICADO: Desenha linha indicadora na posição de referenceDate ***
  useEffect(() => {
    const indicatorGroup = d3
      .select(svgRef.current)
      .select("g.today-indicator"); // Mantido nome do grupo por simplicidade

    if (
      !svgRef.current ||
      !datePickerPosition ||
      width <= 0 ||
      height <= 0 ||
      !xScale
    ) {
      indicatorGroup.selectAll("line.indicator-line").remove(); // Nome da classe da linha ajustado
      return;
    }

    indicatorGroup.selectAll("line.indicator-line").remove();

    const x = datePickerPosition.referenceX; // *** Usa referenceX ***
    const headerHeightEstimate = 30;
    const lineStartY = headerHeightEstimate + 2;
    const lineEndY = height; // Ajustado para considerar padding yScale

    if (isFinite(x) && x >= 0 && x <= width) {
      indicatorGroup
        .append("line")
        .attr("class", "indicator-line") // Nome da classe ajustado
        .attr("x1", x)
        .attr("y1", lineStartY)
        .attr("x2", x)
        .attr("y2", lineEndY)
        .style("stroke", token.colorPrimary)
        .style("stroke-width", 1);
    }
    // *** Depende de datePickerPosition que agora depende de referenceDate ***
  }, [svgRef, datePickerPosition, width, height, xScale, token]);

  // *** MODIFICADO: handleDateChange também atualiza referenceDate ***
  const handleDateChange = (date: Dayjs | null) => {
    if (!date) {
      PubSub.publish("timeline:set-today"); // Volta para hoje/mês e reseta referenceDate via listener
      return;
    }

    const { dateMin: newMin, dateMax: newMax } = calculateDateRange(
      date,
      zoomLevelIndex
    );

    let stateChanged = false;
    if (!dateMin || newMin.getTime() !== dateMin.getTime()) {
      setDateMin(newMin);
      stateChanged = true;
    }
    if (!dateMax || newMax.getTime() !== dateMax.getTime()) {
      setDateMax(newMax);
      stateChanged = true;
    }

    // *** ATUALIZADO: Define a data selecionada como a nova referência ***
    setReferenceDate(date);
  };

  return (
    <div
      style={{
        maxWidth: "100%",
        position: "relative",
        zIndex: 1,
        backgroundColor: token.colorBgContainer,
        border: `${token.lineHeight}px ${token.lineType} ${token.colorBorder}`,
        borderRadius: token.borderRadiusLG,
        overflow: "hidden",
      }}
    >
      {focusedPost && <TimelineD3ChartsTooltip post={focusedPost} />}

      <div>
        {datePickerPosition && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: screens.md ? "10px" : "5px",
              position: "absolute",

              left: screens.md ? `${datePickerPosition.left}px` : "unset",
              right: screens.md ? "unset" : "8px",
              top: screens.md ? `${datePickerPosition.top}px` : "8px",

              width: screens.md ? datePickerPosition.width : "auto",
              zIndex: 10,
              backgroundColor: token.colorBgBase,
              border: screens.md
                ? `1px solid ${token.colorBgElevated}`
                : "unset",
              padding: screens.md ? "12px" : "4px",
              borderRadius: token.borderRadius,
              boxShadow: screens.md ? token.boxShadowSecondary : "none",
              justifyContent: "center",
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={zoomLevelIndex === zoomLevels.length - 1}
              aria-label="Zoom Out"
              style={{
                border: `1px solid ${
                  screens.md ? token.colorBorder : token.colorBorderSecondary
                }`,
                backgroundColor: token.colorBgContainer,
              }}
            />
            <DatePicker
              size="small"
              format={
                localStorage.getItem("dateFormat")
                  ? {
                      format: localStorage.getItem("dateFormat") as string,
                    }
                  : {
                      format: "DD/MM/YYYY",
                    }
              }
              allowClear={false}
              // *** MODIFICADO: Mostra a referenceDate ***
              value={referenceDate}
              onChange={handleDateChange}
              style={{
                width: "110px",
                textAlign: "center",
                flexShrink: 0,
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${
                  screens.md ? token.colorBorder : token.colorBorderSecondary
                }`,
              }}
              bordered={false}
              suffixIcon={null}
            />
            <Button
              type="text"
              size="small"
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={zoomLevelIndex === 0}
              aria-label="Zoom In"
              style={{
                border: `1px solid ${
                  screens.md ? token.colorBorder : token.colorBorderSecondary
                }`,
                backgroundColor: token.colorBgContainer,
              }}
            />
          </div>
        )}
        <TimelineD3ChartsActions
          setViewType={setViewType}
          viewType={viewType}
          selectedCategoryIds={selectedCategoryIds}
          setSelectedCategoryIds={setSelectedCategoryIds}
          onClearAllFilters={handleClearAllFilters}
          isAnyFilterActive={isAnyFilterActive}
        />
      </div>

      <svg
        ref={svgRef}
        width="100%"
        height={timelineHeight}
        style={{ transition: "height ease 400ms", display: "block" }}
      >
        <g className="zoom-rect" />
        <g className="bg" transform="translate(0,0)" />
        <g className="date" transform="translate(0,0)" />
        {/* Mantido nome do grupo, mas linha representa referenceDate */}
        <g className="today-indicator" />
        <g className="event" transform="translate(0,0)" />
      </svg>

      {postForPreview && (
        <ModalPreviewEvent
          open={isPreviewModalOpen}
          onClose={handleClosePreviewModal}
          event={postForPreview}
          interestsData={interestsResponseForModal}
          onEdit={handleEditPreview}
        />
      )}
      {isEditModalOpen && postForEdit && (
        <ModalPostEdit
          post={postForEdit}
          onClose={handleCloseEditModal}
          onUpdate={handlePostUpdate}
        />
      )}
    </div>
  );
}
